import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { t } from '../i18n';
import { Glyph } from './icons';
import { PdfPage, usePdf } from './PdfPage';

type ViewerKind = 'image' | 'video' | 'audio' | 'pdf' | 'file';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;
/** A PDF page is drawn this wide at 100%, like the prototype's sheet. */
const PAGE_BASE_PX = 560;
const VIEWPORT_MARGIN_PX = 32;

const ICON = {
  prev: 'M15 6l-6 6 6 6',
  next: 'M9 6l6 6-6 6',
  plus: 'M12 5v14 M5 12h14',
  minus: 'M5 12h14',
  download: 'M12 4v12 M7 11l5 5 5-5 M5 20h14',
  close: 'M6 6l12 12 M18 6L6 18',
};

/** Round to a quarter, so repeated steps never drift. */
function stepZoom(zoom: number, direction: 1 | -1): number {
  const next = Math.round((zoom + direction * ZOOM_STEP) * 100) / 100;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next));
}

/**
 * The file full screen: a picture to zoom, a PDF to page through, a video or
 * a sound to play. Esc closes it, a click beside the file does too, and focus
 * stays inside until it is closed.
 */
export function MediaLightbox({
  kind,
  source,
  filename,
  meta,
  onClose,
}: {
  kind: ViewerKind;
  source: string;
  filename: string;
  meta: string;
  onClose: () => void;
}): React.ReactElement {
  const [zoom, setZoom] = useState(1);
  const [page, setPage] = useState(1);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pdf = usePdf(kind === 'pdf' ? source : null);
  const pages = pdf.status === 'ready' ? pdf.document.numPages : 0;
  const zoomable = kind === 'image' || kind === 'pdf';

  useEffect(() => {
    const opener = document.activeElement;
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === 'Tab') {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, a[href], video[controls], audio[controls]',
      );
      const first = focusable?.[0];
      const last = focusable?.[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    } else if (kind === 'pdf' && event.key === 'ArrowRight') {
      setPage((current) => Math.min(pages, current + 1));
    } else if (kind === 'pdf' && event.key === 'ArrowLeft') {
      setPage((current) => Math.max(1, current - 1));
    } else if (kind === 'video' && event.key === ' ' && event.target === event.currentTarget) {
      event.preventDefault();
      const video = videoRef.current;
      if (video) void (video.paused ? video.play() : video.pause());
    }
  };

  const pageWidth = Math.min(
    Math.round(PAGE_BASE_PX * zoom),
    Math.round(window.innerWidth * zoom) - VIEWPORT_MARGIN_PX,
  );

  return createPortal(
    // The backdrop closes on a click; the key handling lives on it so Esc
    // works wherever focus is inside.
    <div
      ref={dialogRef}
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t('media.viewer')}
      tabIndex={-1}
      onClick={onClose}
      onKeyDown={onKeyDown}
    >
      <div className="lightbox__bar" onClick={(event) => event.stopPropagation()}>
        <div className="lightbox__title">
          <div className="lightbox__name">{filename}</div>
          <div className="lightbox__meta">{meta}</div>
        </div>
        {kind === 'pdf' && pages > 0 ? (
          <div className="lightbox__group">
            <button
              type="button"
              className="lightbox__button"
              aria-label={t('media.prevPage')}
              title={t('media.prevPage')}
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <Glyph d={ICON.prev} size={20} />
            </button>
            <span className="lightbox__label" aria-live="polite">
              {t('media.pageOf', { page: String(page), pages: String(pages) })}
            </span>
            <button
              type="button"
              className="lightbox__button"
              aria-label={t('media.nextPage')}
              title={t('media.nextPage')}
              disabled={page >= pages}
              onClick={() => setPage((current) => Math.min(pages, current + 1))}
            >
              <Glyph d={ICON.next} size={20} />
            </button>
          </div>
        ) : null}
        {zoomable ? (
          <div className="lightbox__group">
            <button
              type="button"
              className="lightbox__button"
              aria-label={t('media.zoomOut')}
              title={t('media.zoomOut')}
              disabled={zoom <= ZOOM_MIN}
              onClick={() => setZoom((current) => stepZoom(current, -1))}
            >
              <Glyph d={ICON.minus} size={20} />
            </button>
            <span className="lightbox__label lightbox__label--zoom" aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              className="lightbox__button"
              aria-label={t('media.zoomIn')}
              title={t('media.zoomIn')}
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoom((current) => stepZoom(current, 1))}
            >
              <Glyph d={ICON.plus} size={20} />
            </button>
          </div>
        ) : null}
        <a
          className="lightbox__button"
          href={source}
          download={filename}
          aria-label={t('media.download')}
          title={t('media.download')}
        >
          <Glyph d={ICON.download} size={20} />
        </a>
        <button
          type="button"
          className="lightbox__button"
          autoFocus
          aria-label={t('media.close')}
          title={t('media.close')}
          onClick={onClose}
        >
          <Glyph d={ICON.close} size={20} />
        </button>
      </div>

      <div className="lightbox__stage">
        {kind === 'image' ? (
          <img
            className="lightbox__image"
            src={source}
            alt={filename}
            style={{
              maxWidth: `${Math.round(100 * zoom)}%`,
              maxHeight: zoom > 1 ? 'none' : '78vh',
              width: zoom > 1 ? `${Math.round(100 * zoom)}%` : undefined,
            }}
            onClick={(event) => event.stopPropagation()}
          />
        ) : null}
        {kind === 'video' ? (
          <video
            ref={videoRef}
            className="lightbox__video"
            src={source}
            controls
            autoPlay
            onClick={(event) => event.stopPropagation()}
          />
        ) : null}
        {kind === 'pdf' && pdf.status === 'ready' ? (
          <div className="lightbox__sheet" onClick={(event) => event.stopPropagation()}>
            <PdfPage
              document={pdf.document}
              pageNumber={page}
              width={pageWidth}
              label={t('media.pdfPage', { page: String(page), pages: String(pages) })}
              className="lightbox__pdfpage"
            />
          </div>
        ) : null}
        {kind === 'pdf' && pdf.status === 'failed' ? (
          <p className="lightbox__note">{t('media.pdfFailed')}</p>
        ) : null}
        {kind === 'audio' || kind === 'file' ? (
          <div className="lightbox__other" onClick={(event) => event.stopPropagation()}>
            <div className="lightbox__othername">{filename}</div>
            <div className="lightbox__meta">{kind === 'file' ? t('media.noPreview') : meta}</div>
            {kind === 'audio' ? <audio src={source} controls autoPlay /> : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
